import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Grid } from '@mui/material'
import { Edit } from '@mui/icons-material'

import {
  useDELETE_gradeScaleMutation,
  useGET_gradeScaleByIdQuery,
  usePOST_gradeScaleMutation,
  usePUT_gradeScaleMutation,
} from 'redux/api/gradeScaleApi'

import { Form, Formik } from 'formik'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import InputField from 'components/atoms/fields/InputField'

import formModel, { initialValues, validationSchema } from 'utils/formModels/gradeScaleModel'
import { crudColorMap, crudIconMap } from 'utils/helpers'

import { ActionType, IGradeScale } from 'types'
import useCrudData from 'utils/customHooks/useCrudData'
import { useSelector } from 'react-redux'
import { RootState } from 'redux/store'

interface IProps {
  action: ActionType
  id: string
  viewOnly: boolean
}

const GradeScaleForm: FC<IProps> = ({ action, id, viewOnly }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  
  const user = useSelector((state: RootState) => state['user'].user)
  const prefix = ['principal', 'vice_principal', 'staff_administrator'].includes(user?.role) ? "grade-scale" : "view-grade-scale"

  const backToSlug = `/${prefix}`
  const editSlug = `/${prefix}/edit/${id}`

  const {
    isLoading,
    entity,
    handleSubmit,
  } = useCrudData({
    backToSlug,
    entityName: `${t('school.grading-scale')}`,
    entityId: id,
    getEntityQuery: useGET_gradeScaleByIdQuery,
    createEntityMutation: usePOST_gradeScaleMutation,
    editEntityMutation: usePUT_gradeScaleMutation,
    deleteEntityMutation: useDELETE_gradeScaleMutation
  })

  const {
    grade,
    sign,
    numeric,
    text,
    special,
    zpd
  } = formModel()
  
  const serverValues = entity?.id
    ? {
        ...entity
      }
    : null

  return (
    <Formik
      enableReinitialize
      initialValues={serverValues || initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const data: IGradeScale = {
          ...values,
        }

        handleSubmit(action, id, data)
      }}
    >
      <Form>
        <Grid container spacing={4} sx={{ marginTop: '-1rem' }}>
          <Grid item xs={12} md={2}>
            <InputField fieldProps={{ ...grade }} disabled={viewOnly} />
          </Grid>
          <Grid item xs={12} md={2}>
            <InputField fieldProps={{ ...sign }} disabled={viewOnly} />
          </Grid>
          <Grid item xs={12} md={2}>
            <InputField fieldProps={{ ...numeric }} disabled={viewOnly} />
          </Grid>
          <Grid item xs={12} md={2}>
            <InputField fieldProps={{ ...special }} disabled={viewOnly} />
          </Grid>
          <Grid item xs={12} md={2}>
            <InputField fieldProps={{ ...text }} disabled={viewOnly} />
          </Grid>
          <Grid item xs={12} md={2}>
            <InputField fieldProps={{ ...zpd }} disabled={viewOnly} />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex' }} className="transparent-background">
              <Button
                variant='outlined'
                color='black'
                onClick={() => navigate(backToSlug)}
                sx={{ minHeight: '2.5rem', width: '10rem' }}
              >
                <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                {`${t('common.back')}`}
              </Button>
              {action === 'view' && 
                <Box sx={{ marginLeft: 'auto' }}>
                  <Button
                    variant='outlined'
                    color='black'
                    onClick={() => navigate(editSlug)}
                    sx={{ minHeight: '2.5rem', width: '10rem' }}
                  >
                    <Edit style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                    {`${t('common.edit')}`}
                  </Button>
                </Box>
              }
              {action !== 'view' && (
                <SubmitButton
                  color={crudColorMap[action]}
                  text={`${t('form-actions.' + action)}`}
                  icon={crudIconMap[action]}
                  sx={{ width: '10rem' }}
                  isLoading={isLoading}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Form>
    </Formik>
  )
}
export default GradeScaleForm
